import React, { useEffect, useState } from "react";
import { DOMAIN_ID, ORG_ID } from "../../../constants";
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  message,
  Row,
} from "antd";
import {
  getAuthFactors,
  getMFAProvider,
  setAuthFactorsConfig,
} from "../../../service/tenantService";
import RenderIcon from "../../../utils/renderImage";
function AuthFactors() {
  const [notification, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [selectedPrimary, setSelectedPrimary] = useState([]);
  const [selectedSecondary, setSelectedSecondary] = useState([]);
  const [primaryAuth, setPrimaryAuth] = useState([]);
  const [secondaryAuth, setSecondaryAuth] = useState([]);

  useEffect(() => {
    handleSetAuthMethod();
  }, []);

  const handleSetAuthMethod = () => {
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
    };

    const payloadStr = {
      orgId: `${ORG_ID()}`,
      tenantId: `${DOMAIN_ID()}`,
    };

    // 1️ FETCH ALL AVAILABLE MFA METHODS
    getMFAProvider({
      payload,
      onSuccess: (res) => {
        const items = Array.isArray(res?.data) ? res.data : [];

        // PRIMARY list
        const primaryOrder = [37, 38, 34, 35, 36];
        const primary = items
          .filter((item) => item.factorType === "PRIMARY")
          .map((item) => ({
            label: item.name, // Show "SAML2", "OIDC"
            value: item.id, // Value must be ID
          }))
          .sort(
            (a, b) =>
              primaryOrder.indexOf(a.value) - primaryOrder.indexOf(b.value)
          );

        // SECONDARY list
        const secondaryOrder = [32, 29, 39, 28];
        // "Verifiable Credential" was the wallet factor. It is still seeded in
        // mfa_config, but the wallet is gone, so it is not offered here.
        const isRetiredFactor = (item) => item.name === "Verifiable Credential";
        const retiredIds = items.filter(isRetiredFactor).map((item) => item.id);
        const secondary = items
          .filter((item) => item.factorType === "SECONDARY" && !isRetiredFactor(item))
          .map((item) => {
            let label = item.name;

            // Convert "sms" or "SMS" → "SMS"
            if (label.toLowerCase() === "sms") {
              label = "SMS";
            }

            return {
              label,
              value: item.id,
            };
          })
          .sort(
            (a, b) =>
              secondaryOrder.indexOf(a.value) - secondaryOrder.indexOf(b.value)
          );
        setPrimaryAuth(primary);
        setSecondaryAuth(secondary);

        // Auto-select Authnull as default primary factor
        const defaultPrimary = primary.find(
          (p) => p.label.toLowerCase() === "Passwordless"
        );

        if (defaultPrimary) {
          form.setFieldsValue({ primaryAuth: defaultPrimary.value });
          setSelectedPrimary([defaultPrimary.value]);
        }
        // 2️ After setting options, fetch configured selection
        getAuthFactors({
          payload: payloadStr,
          onSuccess: (configRes) => {
            const saved = configRes?.factors || {};

            // Backend mapping → "1" = PRIMARY, "2" = SECONDARY
            const selectedPrimary = saved["1"] || [];
            // Drop the retired wallet factor so the next Update clears it.
            const selectedSecondary = (saved["2"] || []).filter(
              (id) => !retiredIds.includes(id)
            );

            // 3️ Auto-select checkboxes
            form.setFieldsValue({
              primaryAuth: selectedPrimary,
              secondaryAuth: selectedSecondary,
            });
            setSelectedPrimary(selectedPrimary);
            setSelectedSecondary(selectedSecondary);
          },
          onError: (err) => {
            console.error("Failed to get configured MFA:", err?.response?.status ?? err?.status, err?.message);
            message.error("Failed to load configured MFA!");
          },
        });
      },

      onError: (err) => {
        console.error("Failed to load MFA provider:", err?.response?.status ?? err?.status, err?.message);
        message.error("Failed to load MFA provider!");
      },
    });
  };

  const handleSave = async () => {
    try {
      // 1️ Validate form
      const values = await form.validateFields();
      const primary = [Number(values.primaryAuth)];
      const currentSecondary = selectedSecondary;

      // 2️ Build the backend payload
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),

        // Mapping UI → Backend
        factors: {
          1: primary, // PRIMARY
          2: currentSecondary, // SECONDARY
        },
      };
      // 3️ Call API
      setAuthFactorsConfig({
        payload,
        onSuccess: () => {
          message.success("MFA configuration saved successfully!");
        },
        onError: (err) => {
          console.error("Failed to save MFA:", err?.response?.status ?? err?.status, err?.message);
          message.error("Failed to save MFA!");
        },
      });
    } catch (err) {
      console.error("Form validation failed:", err);
    }
  };
  return (
    <>
      {contextHolder}

      <Form form={form} className="px-5 py-2 space-y-5">
        <Row gutter={16} className="flex items-stretch gap-4">
          {/* Primary Authenticator */}
          <Col span={9}>
            <Card
              title="Primary Factor"
              bordered={true}
              className=" custom-card h-full border-gray-500 "
            >
              <Form.Item
                name="primaryAuth"
                rules={[
                  { required: true, message: "Select primary authenticator" },
                ]}
                className="!p-3 !mb-0"
              >
                <div className="flex flex-col gap-3 w-full ">
                  {primaryAuth.map((option) => (
                    <div className="flex items-center justify-between gap-3 p-3 border border-slate-100 rounded-lg hover:border-primary/50 cursor-pointer transition-all bg-slate-50 /50 group">
                    <Checkbox
                      key={option.value}
                      value={option.value}
                      checked={selectedPrimary[0] === option.value} // only one can be checked
                      onChange={() => {
                        setSelectedPrimary([option.value]);
                        form.setFieldsValue({ primaryAuth: option.value });
                      }} // select this, unselect others
                    >
                      {option.label}
                    </Checkbox>
                     <Avatar
                    className="text-[11px] text-blue-500 bg-white"
                    icon={RenderIcon(option.label)}
                  /></div>
                  ))}
                </div>
              </Form.Item>
            </Card>
          </Col>

          {/* Secondary Authenticator */}
          <Col span={9}>
            <Card
              title="Secondary Factor"
              bordered={true}
              className="custom-card h-full border-gray-500 "
            >
              <Form.Item name="secondaryAuth" className="!p-3">
                <div className="flex flex-col gap-3 w-full">
                  {secondaryAuth.map((option) => {
                    return (
                      <div className="flex items-center justify-between gap-3 p-3 border border-slate-100 rounded-lg hover:border-primary/50 cursor-pointer transition-all bg-slate-50 /50 group">
                      <Checkbox
                        key={option.value}
                        checked={selectedSecondary.includes(option.value)}
                        onChange={(e) => {
                          const value = option.value;
                          e.target.checked
                            ? setSelectedSecondary((prev) => [...prev, value])
                            : setSelectedSecondary((prev) =>
                                prev.filter((item) => item !== value)
                              );
                        }}
                      >
                        {option.label}
                      </Checkbox>
                      <Avatar
                    className="text-[11px] text-blue-500 bg-white"
                    icon={RenderIcon(option.label)}
                  />
                      </div>
                    );
                  })}
                </div>
              </Form.Item>
            </Card>
          </Col>
        </Row>
        <div className="flex gap-2 justify-end w-full">
          <Button className="px-7 rounded-lg" onClick={() => handleSave()} type="primary">
            Update
          </Button>
        </div>
      </Form>
    </>
  );
}

export default AuthFactors;
